import _ from "lodash";
/**--external-- */
import React, { Fragment } from "react";

/**--internal-- */
import Table from "UIComponents/GenericTableEditor/Table";
import {
  getFiltersInitialValue,
  getPlannerElementNodeEditorConfig,
  getPlannerElementNodeEditorConfigForNestedChecklist,
} from "./FieldNestedChecklistUtils";
import BenchmarkView from "UIComponents/BenchmarkView";
import EmptyField from "UIComponents/EmptyField";
import GenericTableEditor from "UIComponents/GenericTableEditor";
import I18nHOC from "UIComponents/I18nHOC";
import { generateNestedSelectedData, getPYPElementSetFromNodes } from "Utils";
import {
  getNestedChecklistValue,
  ADD_FIELD_MODAL,
  TEMPLATE_EDIT_MODE,
  NESTED_CHECKLIST_PREVIEW,
} from "Fields/FieldUtils";

/**--relative-- */
import Field from "../Field";
import NestedChecklistEditorModal from "./components/NestedChecklistEditorModal";
import classes from "./FieldNestedChecklist.scss";
import {
  tableContainerStyle,
  editModeEmptyFieldStyles,
  previewModeEmptyFieldStyles,
} from "./FieldNestedChecklistStyles";
import classNames from "classnames";

class FieldNestedChecklist extends Field {
  constructor(props) {
    super(props);

    this.state = {
      showEditModal: false,
    };
  }

  onEditClick = () => {
    this.setState({ showEditModal: true });
  };

  closeEditModal = () => {
    this.setState({ showEditModal: false });
  };

  renderTemplateView = () => {
    const { t } = this.props;
    return (
      <EmptyField
        title={t("common:select_with_label", {
          label: t("common:list_item_other"),
        })}
        containerStyle={previewModeEmptyFieldStyles.containerStyle}
        showAdd
      />
    );
  };

  renderView = () => {
    const { fieldObj, emptyText, nodeLabelStyle } = this.props;
    const nodes = getNestedChecklistValue({ valueObj: fieldObj });

    return (
      <BenchmarkView
        value={generateNestedSelectedData({
          ...getPYPElementSetFromNodes({ nodes }),
          value: _.get(fieldObj, "value", []),
        })}
        emptyText={emptyText}
        mode="view"
        nodeLabelStyle={nodeLabelStyle}
      />
    );
  };

  renderEdit = () => {
    const {
      plannerElementSet,
      emptyText,
      fieldObj,
      t,
      label,
      options,
    } = this.props;
    const { nodes } = plannerElementSet;

    if (_.isEmpty(nodes)) {
      return <EmptyField title={emptyText} />;
    }

    const value = _.get(fieldObj, "value", []);

    const resolvedNodes = getNestedChecklistValue({ valueObj: fieldObj });

    return (
      <Fragment>
        <div className={classes.editContainer}>
          {_.isEmpty(resolvedNodes) ? (
            <EmptyField
              title={t("common:select_with_label", {
                label: t("common:list_item_other"),
              })}
              containerStyle={editModeEmptyFieldStyles.containerStyle}
              onAddNewClick={this.onEditClick}
              showAdd
            />
          ) : (
            <div className={classes.contentContainer}>
              <BenchmarkView
                value={generateNestedSelectedData({
                  ...getPYPElementSetFromNodes({ nodes: resolvedNodes }),
                  value,
                })}
                mode="view"
              />
            </div>
          )}
        </div>
        {this.state.showEditModal ? (
          <NestedChecklistEditorModal
            plannerElementSet={plannerElementSet}
            closeModal={this.closeEditModal}
            headerLabel={label}
            value={value}
            taggedNodesWithAncestors={resolvedNodes}
            filtersInitialValue={getFiltersInitialValue({
              grades: _.get(options, "grades", []),
            })}
            updateValue={value => {
              this.updateValue(value, { nodes });
            }}
          />
        ) : null}
      </Fragment>
    );
  };

  renderPreview = () => {
    const { previewFor } = this.props;
    const previewClass = classNames({
      [classes.disabled]: previewFor !== NESTED_CHECKLIST_PREVIEW,
    });
    return <div className={previewClass}>{this.renderPreviewContent()}</div>;
  };

  renderPreviewContent = () => {
    const { plannerElementSetConfig, data, previewFor } = this.props;
    switch (previewFor) {
      case ADD_FIELD_MODAL: {
        return (
          <Table
            mode="view"
            maxDepth={3}
            rootNodeId="-1"
            tableContainerStyle={tableContainerStyle}
            currentEditorConfig={getPlannerElementNodeEditorConfig({
              plannerElementSetConfig,
            })}
            nodes={_.get(data, "nodes", {})}
            gradeOptions={_.get(data, "gradeOptions", [])}
            filteredNodeIds={_.get(data, "filteredNodeIds", {})}
            tagOptions={_.get(data, "tagOptions", [])}
            scrollBehaviourRef={{}}
            setNodesRef={_.noop}
            setSelectedSidebarNodes={_.noop}
          />
        );
      }
      case TEMPLATE_EDIT_MODE: {
        return this.renderTemplateView();
      }
      case NESTED_CHECKLIST_PREVIEW: {
        const nodes = _.get(data, "nodes", {});
        const rootNode = _.get(nodes, "-1");

        return (
          <GenericTableEditor
            mode="view"
            rootNode={rootNode}
            nodes={nodes}
            currentEditorConfig={getPlannerElementNodeEditorConfigForNestedChecklist(
              {
                isForPreview: true,
                plannerElementSetConfig,
              }
            )}
            tagOptions={_.get(data, "tagOptions", [])}
            nodeType={"NESTED_CHECKLIST"}
            maxDepth={5}
            showHeader={false}
            warningMessage={this.props.warningMessage}
          />
        );
      }
    }
  };
}

export default I18nHOC({ resource: ["common"] })(FieldNestedChecklist);
FieldNestedChecklist.displayName = "FieldNestedChecklist";
